import React, { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Package, Loader2 } from 'lucide-react';

const CATEGORIES = [
  { value: 'protein', label: 'Protein' },
  { value: 'bcaa', label: 'BCAA' },
  { value: 'creatine', label: 'Creatine' },
  { value: 'vitamin', label: 'Vitamin' },
  { value: 'pre_workout', label: 'Pre-Workout' },
  { value: 'accessory', label: 'Accessory' },
  { value: 'other', label: 'Other' },
];

const UNITS = [
  { value: 'kg', label: 'kg' },
  { value: 'g', label: 'g' },
  { value: 'capsules', label: 'Capsules' },
  { value: 'sachets', label: 'Sachets' },
  { value: 'pieces', label: 'Pieces' },
  { value: 'bottles', label: 'Bottles' },
];

const CATEGORY_COLORS = {
  protein: 'bg-blue-100 text-blue-700',
  bcaa: 'bg-green-100 text-green-700',
  creatine: 'bg-purple-100 text-purple-700',
  vitamin: 'bg-orange-100 text-orange-700',
  pre_workout: 'bg-red-100 text-red-700',
  accessory: 'bg-gray-100 text-gray-700',
  other: 'bg-slate-100 text-slate-700',
};

const formatINR = (price) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(price || 0);

const emptyForm = { name: '', category: '', unit: '', cost_price: '', description: '' };

export default function ProductCatalogTab({ isAdmin }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, product: null });
  const [deleting, setDeleting] = useState(false);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/supplements/products');
      setProducts(res.data?.products || []);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const openCreateDialog = () => {
    setSelectedProduct(null);
    setFormData(emptyForm);
    setShowDialog(true);
  };

  const openEditDialog = (product) => {
    setSelectedProduct(product);
    setFormData({
      name: product.name || '',
      category: product.category || '',
      unit: product.unit || '',
      cost_price: product.cost_price?.toString() || '',
      description: product.description || '',
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) { toast.error('Product name is required'); return; }
    if (!formData.category) { toast.error('Category is required'); return; }
    if (!formData.unit) { toast.error('Unit is required'); return; }
    if (!formData.cost_price || isNaN(Number(formData.cost_price)) || Number(formData.cost_price) <= 0) {
      toast.error('Valid cost price is required'); return;
    }

    setSubmitting(true);
    try {
      const body = {
        name: formData.name.trim(),
        category: formData.category,
        unit: formData.unit,
        cost_price: Number(formData.cost_price),
        description: formData.description.trim() || null,
      };

      if (selectedProduct) {
        await axiosInstance.put(`/supplements/products/${selectedProduct.id}`, body);
        toast.success('Product updated');
      } else {
        await axiosInstance.post('/supplements/products', body);
        toast.success('Product created');
      }
      setShowDialog(false);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save product');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.product) return;
    setDeleting(true);
    try {
      await axiosInstance.delete(`/supplements/products/${deleteDialog.product.id}`);
      toast.success('Product deactivated');
      setDeleteDialog({ open: false, product: null });
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete product');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">Product Catalog</h2>
          <p className="text-sm text-muted-foreground">{products.length} active products</p>
        </div>
        {isAdmin && (
          <Button onClick={openCreateDialog} data-testid="add-product-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Product
          </Button>
        )}
      </div>

      {/* Product Table */}
      {products.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Package className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-xl font-semibold mb-2">No Products Yet</h3>
            <p className="text-muted-foreground">
              {isAdmin ? 'Click "Add Product" to create your first supplement product.' : 'No products have been added yet.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-800 text-white">
                    <th className="text-left px-4 py-3 text-sm font-medium">Name</th>
                    <th className="text-left px-4 py-3 text-sm font-medium">Category</th>
                    <th className="text-left px-4 py-3 text-sm font-medium">Unit</th>
                    <th className="text-right px-4 py-3 text-sm font-medium">Cost Price</th>
                    {isAdmin && <th className="text-center px-4 py-3 text-sm font-medium">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {products.map((product) => (
                    <tr key={product.id} className="hover:bg-slate-50" data-testid={`product-row-${product.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-800">{product.name}</div>
                        {product.description && (
                          <div className="text-xs text-slate-500 mt-0.5">{product.description}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className={CATEGORY_COLORS[product.category] || CATEGORY_COLORS.other}>
                          {CATEGORIES.find(c => c.value === product.category)?.label || product.category}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {UNITS.find(u => u.value === product.unit)?.label || product.unit}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-slate-800">
                        {formatINR(product.cost_price)}
                      </td>
                      {isAdmin && (
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(product)}
                              data-testid={`edit-product-${product.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeleteDialog({ open: true, product })}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-product-${product.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedProduct ? 'Edit Product' : 'Add Product'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g. Whey Protein 1kg"
                data-testid="product-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Category *</Label>
              <Select
                value={formData.category}
                onValueChange={(val) => setFormData(prev => ({ ...prev, category: val }))}
              >
                <SelectTrigger data-testid="product-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Unit *</Label>
              <Select
                value={formData.unit}
                onValueChange={(val) => setFormData(prev => ({ ...prev, unit: val }))}
              >
                <SelectTrigger data-testid="product-unit-select">
                  <SelectValue placeholder="Select unit" />
                </SelectTrigger>
                <SelectContent>
                  {UNITS.map(unit => (
                    <SelectItem key={unit.value} value={unit.value}>{unit.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Cost Price (INR) *</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={formData.cost_price}
                onChange={(e) => setFormData(prev => ({ ...prev, cost_price: e.target.value }))}
                placeholder="0.00"
                data-testid="product-cost-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Optional description"
                data-testid="product-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={submitting} data-testid="save-product-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {submitting ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, product: deleteDialog.product })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Deactivate Product</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              This will deactivate the product <strong>"{deleteDialog.product?.name}"</strong>.
              It will no longer appear in the catalog or be available for new stacks.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, product: null })}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting} data-testid="confirm-delete-product-btn">
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {deleting ? 'Deleting...' : 'Deactivate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
